/* eslint-disable max-lines */
import { ThemeProvider } from "@mui/material";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useRouter } from "next/router";
import type { FunctionComponent, PartialPropsOf, ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { Required } from "utility-types";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { article as articleData } from "shared/data";
import { articleSponsorType } from "shared/data/article";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { TakeoverAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot";
import { ArticleImageGallery } from "scmp-app/components/article/article-image-gallery";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import { useArticlePianoPaywallContext } from "scmp-app/components/article/article-piano-paywall/contexts";
import type { Props as ArticlePrintHeadlineMessageProps } from "scmp-app/components/article/article-print-headline-message";
import { ArticleSpeechPlayerPortal } from "scmp-app/components/article/article-speech/article-speech-player-portal";
import { AuthorAvatarContextProvider } from "scmp-app/components/author/author-avatar/contexts";
import type { ContentBody } from "scmp-app/components/content/content-body";
import { useContentReadMoreContext } from "scmp-app/components/content/content-read-more";
import { ContentReadMoreButton } from "scmp-app/components/content/content-read-more/content-read-more-button";
import { useContentSchemaRenderIframe } from "scmp-app/components/schema-render/common/iframe/hooks";
import { type Props as InlineAdSlotProps } from "scmp-app/components/schema-render/common/inline-ad-slot";
import { appAtom } from "scmp-app/lib/app/atoms";
import { usePaywallPosition } from "scmp-app/lib/rosetta/hooks/rosetta-paywall-position";
import type { articleRenderArticle$key } from "scmp-app/queries/__generated__/articleRenderArticle.graphql";

import {
  InlineAdSlotTagMap,
  MobileInlineAdTestPlanSlotAdTagMap,
  NumberOfMobileInlineAdTestPlanMoreReadableElements,
  NumberOfMoreReadableElements,
} from "./consts";
import { useArticleRenderContext } from "./contexts";
import {
  DynamicGenericArticle,
  DynamicInfographicArticle,
  DynamicLiveArticle,
  DynamicMorningStudioArticle,
  DynamicNewsletterArticle,
  DynamicPhotoEssayAndLongReadArticle,
  DynamicPlusGenericArticle,
  DynamicPostMagazineArticle,
  DynamicSeriesArticle,
  DynamicStyleArticle,
} from "./dynamics";
import { GlobalChecker } from "./global-checker";
import { checkArticleRenderType, getArticleType } from "./helpers";
import { withArticleRenderContext } from "./hoc";
import { useMostLikelyToSubscribe, usePianoIntegration } from "./hooks";
import { useArticleBaseAdvertisingInfo } from "./hooks/article-base-advertising-info";
import type { InlinePaywallOverlayerProps } from "./styles";
import {
  ArticleRenderContainer,
  ContentReadMoreButtonContainer,
  InlinePaywallOverlayer,
  PrimaryContentBody,
  SecondlyContentBody,
  StyledAdSlot,
  StyledArticleActionBar,
  StyledArticleActionWidget,
  StyledArticleAdditionalInfoBox,
  StyledArticleAuthorAvatars,
  StyledArticleAuthorBlock,
  StyledArticleAuthorNames,
  StyledArticleAuthorsDetails,
  StyledArticleAuthorShortBio,
  StyledArticleBeforeYouGoWidget,
  StyledArticleCarouselWidget,
  StyledArticleCommentsTrigger,
  StyledArticleCorrectionsWidget,
  StyledArticleDate,
  StyledArticleFurtherReadingWidget,
  StyledArticleLeading,
  StyledArticleLiveTag,
  StyledArticleMoreOnThisList,
  StyledArticlePaywallContainer,
  StyledArticleReadingTime,
  StyledArticleRelatedTopics,
  StyledArticleScmpPollWidget,
  StyledArticleSections,
  StyledArticleSeries,
  StyledArticleShareWidget,
  StyledArticleSpeechInlineWidget,
  StyledArticleSponsor,
  StyledArticleTopic,
  StyledArticleTopicFollowWidget,
  StyledArticleTopics,
  StyledArticleTopicWidget,
  StyledArticleVocabBox,
  StyledCommentContainer,
  StyledContentHeadline,
  StyledContentHeadlineTag,
  StyledContentSubHeadline,
} from "./styles";
import { theme as defaultTheme } from "./theme";
import { ArticleRenderType } from "./types";
export * from "./types";

export type DefaultComponentsProvider = {
  actionBar: (props?: PartialPropsOf<typeof StyledArticleActionBar>) => ReactNode;
  actionWidget: (props?: PartialPropsOf<typeof StyledArticleActionWidget>) => ReactNode;
  adslot: (props: Required<PartialPropsOf<typeof StyledAdSlot>, "adUnit" | "sizes">) => ReactNode;
  articleAdditionalInfoBox: (
    props?: PartialPropsOf<typeof StyledArticleAdditionalInfoBox>,
  ) => ReactNode;
  authorAvatars: (props?: PartialPropsOf<typeof StyledArticleAuthorAvatars>) => ReactNode;
  authorBlock: (props?: PartialPropsOf<typeof StyledArticleAuthorBlock>) => ReactNode;
  authorDetails: (props?: PartialPropsOf<typeof StyledArticleAuthorsDetails>) => ReactNode;
  authorNames: (props?: PartialPropsOf<typeof StyledArticleAuthorNames>) => ReactNode;
  authorShortBio: (props?: PartialPropsOf<typeof StyledArticleAuthorShortBio>) => ReactNode;
  beforeYouGoWidget: (
    provider: DefaultComponentsProvider,
    props?: PartialPropsOf<typeof StyledArticleBeforeYouGoWidget>,
  ) => ReactNode;
  body: (
    props?: PartialPropsOf<typeof ContentBody> & {
      isDisabledArticleImageGallery?: boolean;
      paywallVariant?: InlinePaywallOverlayerProps["$variant"];
      printHeadlineVariant?: ArticlePrintHeadlineMessageProps["variant"];
    },
  ) => ReactNode;
  bottomCarouselWidget: (
    provider: DefaultComponentsProvider,
    props?: PartialPropsOf<typeof StyledArticleCarouselWidget>,
  ) => ReactNode;
  commentTrigger: (props?: PartialPropsOf<typeof StyledArticleCommentsTrigger>) => ReactNode;
  correction: (props?: PartialPropsOf<typeof StyledArticleCorrectionsWidget>) => ReactNode;
  furtherReading: (props?: PartialPropsOf<typeof StyledArticleFurtherReadingWidget>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof StyledContentHeadline>) => ReactNode;
  headlineTag: (props?: PartialPropsOf<typeof StyledContentHeadlineTag>) => ReactNode;
  leading: (
    props?: PartialPropsOf<typeof StyledArticleLeading> & { withExternalGallery?: boolean },
  ) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof StyledArticleLiveTag>) => ReactNode;
  moreOnThis: (props?: PartialPropsOf<typeof StyledArticleMoreOnThisList>) => ReactNode;
  paywall: (props?: PartialPropsOf<typeof StyledArticlePaywallContainer>) => ReactNode;
  publishedDate: (props?: PartialPropsOf<typeof StyledArticleDate>) => ReactNode;
  readingTime: (props?: PartialPropsOf<typeof StyledArticleReadingTime>) => ReactNode;
  relatedTopics: (props?: PartialPropsOf<typeof StyledArticleRelatedTopics>) => ReactNode;
  scmpPollWidget: (props?: PartialPropsOf<typeof StyledArticleScmpPollWidget>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof StyledArticleSections>) => ReactNode;
  series: (props?: PartialPropsOf<typeof StyledArticleSeries>) => ReactNode;
  shareWidget: (props?: PartialPropsOf<typeof StyledArticleShareWidget>) => ReactNode;
  speechPlayer: (props: PartialPropsOf<typeof StyledArticleSpeechInlineWidget>) => ReactNode;
  speechPlayerPortal: (props: PartialPropsOf<typeof ArticleSpeechPlayerPortal>) => ReactNode;
  sponsor: (props?: PartialPropsOf<typeof StyledArticleSponsor>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof StyledContentSubHeadline>) => ReactNode;
  takeOverAdSlot: (props?: PartialPropsOf<typeof TakeoverAdSlot>) => ReactNode;
  topic: (props?: PartialPropsOf<typeof StyledArticleTopic>) => ReactNode;
  topicFollowWidget: (props?: PartialPropsOf<typeof StyledArticleTopicFollowWidget>) => ReactNode;
  topics: (props?: PartialPropsOf<typeof StyledArticleTopics>) => ReactNode;
  topicWidget: (props?: PartialPropsOf<typeof StyledArticleTopicWidget>) => ReactNode;
  vocabBox: (props?: PartialPropsOf<typeof StyledArticleVocabBox>) => ReactNode;
};

export type Props = {
  forceRenderInType?: ArticleRenderType;
  reference: articleRenderArticle$key;
};

const Component: FunctionComponent<Props> = ({ forceRenderInType, reference }) => {
  const article = useFragment(
    graphql`
      fragment articleRenderArticle on Article
      @argumentDefinitions(
        customContents: { type: "[CustomContentInput]", defaultValue: [] }
        bodyType: { type: "BodyFormatTypeEnum", defaultValue: PARAGRAPH }
      ) {
        entityId
        sponsorType
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
          }
        }
        ...articleActionWidgetArticle
        ...articleAdditionalInfoBoxArticleContent
        ...articleAuthorBlockArticle
        ...articleAuthorDetailsContent
        ...articleBaseAdvertisingInfoArticle
        ...articleBeforeYouGoWidget
        ...articleCommentsTriggerArticle
        ...articleCorrectionsWidgetArticle
        ...articleDateContent
        ...articleFurtherReadingWidgetArticle
        ...articleImageGalleryContent
        ...articleLeadingArticle
        ...articleMoreOnThisListArticle
        ...articlePianoPaywallContent
        ...articlePrintHeadlineMessageArticle
        ...articleReadingTimeContent
        ...articleRelatedTopicsContent
        ...articleSectionsContent
        ...articleSeriesArticle
        ...articleShareWidgetArticle
        ...articleSpeechInlineWidgetArticle
        ...articleSpeechPlayerPortalArticle
        ...articleSponsorContent
        ...articleTopicContent
        ...articleTopicFollowWidgetArticle
        ...articleTopicsContent
        ...articleTopicWidgetArticle
        ...articleVocabBoxArticle
        ...authorAvatarsArticle
        ...authorNamesArticle
        ...authorShortBioArticle
        ...contentActionBarContent
        ...contentBodyContent @arguments(customContents: $customContents, bodyType: $bodyType)
        ...contentHeadlineContent
        ...contentHeadlineTagContent
        ...contentLiveTagContent
        ...contentReadMoreButtonContent
        ...contentSchemaRenderContent
        ...contentSubHeadlineContent
        ...contentSummaryContent
        ...genericArticleContent
        ...globalCheckerContent
        ...helpersCheckArticleIsInSectionsArticle
        ...helpersCheckArticleRenderTypeArticle
        ...helpersGetArticleTypeArticle
        ...infographicArticleArticle
        ...liveArticleArticle
        ...morningStudioArticleContent
        ...newsletterArticleArticle
        ...pianoIntegrationArticle
        ...plusColumnsArticleContent
        ...plusGenericArticleContent
        ...postMagazineArticleContent
        ...seriesArticleArticle
        ...styleArticleContent
      }
    `,
    reference,
  );

  usePianoIntegration(article);
  const advertising = useArticleBaseAdvertisingInfo(article);
  const articleType = getArticleType(article);
  const { advertisementConfig, navigatorInfo } = useAtomValue(appAtom);
  const isBot = useMemo(() => navigatorInfo?.isBot ?? false, [navigatorInfo?.isBot]);
  const isLightHouseCrawler = useMemo(
    () => navigatorInfo?.isLightHouseCrawler ?? false,
    [navigatorInfo?.isLightHouseCrawler],
  );
  const router = useRouter();

  const renderType = useMemo(
    () => forceRenderInType ?? checkArticleRenderType(article, router.query),
    [article, forceRenderInType, router.query],
  );

  const firstSectionEntityIds = useMemo(
    () => first(article?.sectionsV2)?.value?.map(section => section?.entityId ?? "") ?? [],
    [article?.sectionsV2],
  );

  const isMobileInlineAdTestTargetMainSection = useMemo(
    () =>
      firstSectionEntityIds.some(entityId =>
        [
          ...advertisementConfig?.mobileInlineAdTest?.fourthAdDisabledSectionIds,
          ...advertisementConfig?.mobileInlineAdTest?.fourthAdEnabledSectionIds,
        ].includes(entityId),
      ),
    [
      advertisementConfig?.mobileInlineAdTest?.fourthAdDisabledSectionIds,
      advertisementConfig?.mobileInlineAdTest?.fourthAdEnabledSectionIds,
      firstSectionEntityIds,
    ],
  );

  const isAppConfigMobileArticleInlineAdTestPlanEnabled =
    advertisementConfig?.mobileInlineAdTest?.isEnabled;
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const isMobileArticleInlineAdTestPlanEnabled =
    isAppConfigMobileArticleInlineAdTestPlanEnabled &&
    isMobileInlineAdTestTargetMainSection &&
    !isDesktopUp;

  const targetReadMoreSeparateIndexAdUnit = isMobileArticleInlineAdTestPlanEnabled
    ? "outstream1"
    : "m_banner1";
  const defaultReadMoreSeparateIndexAdSlotTag = isMobileArticleInlineAdTestPlanEnabled
    ? MobileInlineAdTestPlanSlotAdTagMap[1]
    : InlineAdSlotTagMap[1];
  const numberOfMoreReadableElements = isMobileArticleInlineAdTestPlanEnabled
    ? NumberOfMobileInlineAdTestPlanMoreReadableElements
    : NumberOfMoreReadableElements;

  const { isMostLikelyToSubscribeUser } = useMostLikelyToSubscribe();
  const { articlePianoPaywallStateMap } = useArticlePianoPaywallContext();

  const isShowInlinePaywall = useMemo(() => {
    if (articleSponsorType.has(article.sponsorType ?? "")) return false;
    if (articlePianoPaywallStateMap?.[article.entityId] === undefined) return;

    return (
      articlePianoPaywallStateMap[article.entityId] === "requiresInlinePaywall" ||
      articlePianoPaywallStateMap[article.entityId] === "shownInlinePaywall"
    );
  }, [article.entityId, article.sponsorType, articlePianoPaywallStateMap]);

  const isInlinePaywallDomChanged = useMemo(
    () => articlePianoPaywallStateMap?.[article.entityId] === "shownInlinePaywall",
    [article.entityId, articlePianoPaywallStateMap],
  );

  const { getPaywallPosition } = usePaywallPosition(article);
  const isSponsorArticle = articleSponsorType.has(article.sponsorType ?? "");
  const { ComponentWithExtraProps: IframeWithAutoPlayDisabled } = useContentSchemaRenderIframe({
    enableAutoPlay: isSponsorArticle,
    isMuteVideo: isSponsorArticle || !isDesktopUp,
  });

  const { isReadMoreClicked, setIsReadMoreClicked } = useContentReadMoreContext();
  const { articleElementReference, setEntityId, setIsContentReady } = useArticleRenderContext();

  const provider = useMemo<DefaultComponentsProvider>(
    () => ({
      actionBar: props => <StyledArticleActionBar reference={article} {...props} />,
      actionWidget: props => <StyledArticleActionWidget reference={article} {...props} />,
      adslot: props => (
        <StyledAdSlot isDisabled={isShowInlinePaywall === undefined} {...advertising} {...props} />
      ),
      articleAdditionalInfoBox: props => (
        <StyledArticleAdditionalInfoBox contentReference={article} {...props} />
      ),
      authorAvatars: props => <StyledArticleAuthorAvatars reference={article} {...props} />,
      authorBlock: props => <StyledArticleAuthorBlock reference={article} {...props} />,
      authorDetails: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleAuthorsDetails reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      authorNames: props => <StyledArticleAuthorNames reference={article} {...props} />,
      authorShortBio: props => <StyledArticleAuthorShortBio reference={article} {...props} />,
      beforeYouGoWidget: (provider, props) => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleBeforeYouGoWidget provider={provider} reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      body: props => {
        const { primary: primaryInlineAdSlots, secondary: secondInlineAdSlots } = Object.entries(
          props?.inlineAdSlots ?? {},
        ).reduce(
          (mapping, [key, adSlot]) => {
            if (adSlot) {
              const isPrimary = adSlot.isDisabled === undefined;
              const slot = {
                ...adSlot,
                isDisabled: adSlot?.isDisabled === true || isShowInlinePaywall === undefined,
              } as InlineAdSlotProps["slot"];

              if (isPrimary) mapping.primary[key] = slot;
              mapping.secondary[key] = slot;
            }
            return mapping;
          },
          {
            primary: {} as Record<string, InlineAdSlotProps["slot"]>,
            secondary: {} as Record<string, InlineAdSlotProps["slot"]>,
          },
        );

        const getReadMoreSeparateIndex = (schema: Schema) => {
          const [targetAdSlotTag] = Object.entries(props?.inlineAdSlots ?? {}).find(
            ([_, slot]) => slot?.adUnit === targetReadMoreSeparateIndexAdUnit,
          ) ?? [defaultReadMoreSeparateIndexAdSlotTag];
          const targetAdSlotIndex = schema.findIndex(
            node => node.type === "inline-ad-slot" && node.attribs?.tag === targetAdSlotTag,
          );
          return targetAdSlotIndex === -1 ? numberOfMoreReadableElements : targetAdSlotIndex + 1;
        };

        const handleRenderContentBody = () => (
          <>
            <PrimaryContentBody
              {...props}
              advertising={advertising}
              extraComponentMap={{
                iframe: IframeWithAutoPlayDisabled,
                ...props?.extraComponentMap,
              }}
              inlineAdSlots={primaryInlineAdSlots}
              preprocessSchema={schema => {
                const templateProcessedSchema = props?.preprocessSchema?.(schema) ?? schema;
                const readMoreSeparateIndex = getReadMoreSeparateIndex(templateProcessedSchema);

                if (isBot || isLightHouseCrawler) {
                  return templateProcessedSchema.slice(
                    0,
                    readMoreSeparateIndex + numberOfMoreReadableElements,
                  );
                }
                if (articleType === articleData.types.live.entityId) return templateProcessedSchema;

                const defaultPaywallPosition = readMoreSeparateIndex - 1;
                const paywallPosition =
                  getPaywallPosition(templateProcessedSchema) ?? defaultPaywallPosition;

                return isShowInlinePaywall
                  ? templateProcessedSchema.slice(0, paywallPosition)
                  : templateProcessedSchema.slice(
                      0,
                      readMoreSeparateIndex + numberOfMoreReadableElements,
                    );
              }}
              reference={article}
            >
              {!isBot && !isLightHouseCrawler && isShowInlinePaywall && (
                <InlinePaywallOverlayer
                  $height={isMostLikelyToSubscribeUser ? "small" : "normal"}
                  $variant={props?.paywallVariant}
                />
              )}
              {/* Prevent show read more before paywall has been executed */}
              {isShowInlinePaywall === false && (
                <ContentReadMoreButtonContainer>
                  <ContentReadMoreButton reference={article} />
                </ContentReadMoreButtonContainer>
              )}
            </PrimaryContentBody>
            <SecondlyContentBody
              {...props}
              $isShow={isReadMoreClicked}
              advertising={advertising}
              extraComponentMap={{
                iframe: IframeWithAutoPlayDisabled,
                ...props?.extraComponentMap,
              }}
              inlineAdSlots={secondInlineAdSlots}
              preprocessSchema={schema => {
                if (
                  articleType === articleData.types.live.entityId ||
                  (!isBot &&
                    !isLightHouseCrawler &&
                    renderType !== ArticleRenderType.Plus &&
                    isShowInlinePaywall !== false)
                )
                  return [];

                const templateProcessedSchema = props?.preprocessSchema?.(schema) ?? schema;
                const readMoreSeparateIndex = getReadMoreSeparateIndex(templateProcessedSchema);
                return templateProcessedSchema.slice(
                  readMoreSeparateIndex + numberOfMoreReadableElements,
                );
              }}
              reference={article}
            >
              {/* <ArticlePrintHeadlineMessage
                reference={article}
                variant={props?.printHeadlineVariant}
              /> */}
            </SecondlyContentBody>
          </>
        );

        const contentBody = handleRenderContentBody();
        if (props?.isDisabledArticleImageGallery === true) return contentBody;
        return <ArticleImageGallery reference={article}>{contentBody}</ArticleImageGallery>;
      },
      bottomCarouselWidget: (provider, props) => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleCarouselWidget
              {...props}
              items={
                props?.items ?? [
                  provider?.beforeYouGoWidget(provider, {
                    children: provider?.scmpPollWidget({
                      adSlotProps: {
                        breakpoint: theme.breakpoints.down("desktop"),
                        zone: advertising.zone,
                      },
                    }),
                  }),
                  provider?.topicWidget({
                    inlineAds: provider.adslot({
                      adUnit: "m_native2d",
                      breakpoint: theme.breakpoints.down("desktop"),
                      sizes: ["fluid"],
                      zone: advertising.zone,
                    }),
                  }),
                ]
              }
            />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      commentTrigger: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledCommentContainer>
              <StyledArticleCommentsTrigger reference={article} variant="banner" {...props} />
            </StyledCommentContainer>
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      correction: props => <StyledArticleCorrectionsWidget reference={article} {...props} />,
      furtherReading: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleFurtherReadingWidget reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      headline: props => <StyledContentHeadline componentTag="h1" reference={article} {...props} />,
      headlineTag: props => <StyledContentHeadlineTag reference={article} {...props} />,
      leading: props =>
        props?.withExternalGallery || props?.skipGallery ? (
          <StyledArticleLeading
            responsiveVariants={{
              desktopUp: ImageSize["1200x800"],
              mobileUp: ImageSize["768x768"],
              tabletUp: ImageSize["1020x680"],
            }}
            {...props}
            advertising={{
              ...advertising,
              ...props?.advertising,
            }}
            reference={article}
          />
        ) : (
          <ArticleImageGallery reference={article}>
            <StyledArticleLeading
              responsiveVariants={{
                desktopUp: ImageSize["1200x800"],
                mobileUp: ImageSize["768x768"],
                tabletUp: ImageSize["1020x680"],
              }}
              {...props}
              advertising={{
                ...advertising,
                ...props?.advertising,
              }}
              reference={article}
            />
          </ArticleImageGallery>
        ),
      liveTag: props => <StyledArticleLiveTag reference={article} {...props} />,
      moreOnThis: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleMoreOnThisList reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      paywall: props => (
        <StyledArticlePaywallContainer isShowInlinePaywall={isShowInlinePaywall} {...props} />
      ),
      publishedDate: props => <StyledArticleDate reference={article} {...props} />,
      readingTime: props => <StyledArticleReadingTime reference={article} {...props} />,
      relatedTopics: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleRelatedTopics reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      scmpPollWidget: props => <StyledArticleScmpPollWidget {...props} />,
      sections: props => <StyledArticleSections reference={article} {...props} />,
      series: props => <StyledArticleSeries reference={article} {...props} />,
      shareWidget: props => <StyledArticleShareWidget reference={article} {...props} />,
      speechPlayer: props => (
        <StyledArticleSpeechInlineWidget
          onClick={() => {
            setIsReadMoreClicked?.(true);
          }}
          reference={article}
          variant="generic"
          {...props}
        />
      ),
      speechPlayerPortal: props => (
        <ArticleSpeechPlayerPortal
          onClick={() => {
            setIsReadMoreClicked?.(true);
          }}
          reference={article}
          variant="generic-minify"
          {...props}
        />
      ),
      sponsor: props => <StyledArticleSponsor reference={article} {...props} />,
      subHeadline: props =>
        !(isShowInlinePaywall === true && isMostLikelyToSubscribeUser) && (
          <StyledContentSubHeadline reference={article} {...props} />
        ),
      takeOverAdSlot: props => <TakeoverAdSlot adSlotProps={advertising} {...props} />,
      topic: props => <StyledArticleTopic reference={article} {...props} />,
      topicFollowWidget: props => <StyledArticleTopicFollowWidget reference={article} {...props} />,
      topics: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleTopics reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      topicWidget: props => (
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <StyledArticleTopicWidget reference={article} {...props} />
          </GlobalChecker>
        </ClientSideSuspense>
      ),
      vocabBox: props => <StyledArticleVocabBox reference={article} {...props} />,
    }),
    [
      advertising,
      article,
      articleType,
      defaultReadMoreSeparateIndexAdSlotTag,
      getPaywallPosition,
      isBot,
      IframeWithAutoPlayDisabled,
      isLightHouseCrawler,
      isMostLikelyToSubscribeUser,
      isReadMoreClicked,
      isShowInlinePaywall,
      numberOfMoreReadableElements,
      renderType,
      setIsReadMoreClicked,
      targetReadMoreSeparateIndexAdUnit,
    ],
  );

  const handleRenderApplicationArticle = useCallback(() => {
    switch (renderType) {
      case ArticleRenderType.Generic:
        return (
          <DynamicGenericArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.Infographic:
      case ArticleRenderType.InfographicLongRead:
        return (
          <DynamicInfographicArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.Live:
        return (
          <DynamicLiveArticle
            isInlinePaywallDomChanged={isInlinePaywallDomChanged}
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.LongRead:
      case ArticleRenderType.PhotoEssay:
        return (
          <DynamicPhotoEssayAndLongReadArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.MorningStudio:
        return <DynamicMorningStudioArticle provider={provider} reference={article} />;
      case ArticleRenderType.Newsletter:
        return <DynamicNewsletterArticle provider={provider} reference={article} />;
      case ArticleRenderType.Plus:
        return (
          <DynamicPlusGenericArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.PostMagazine:
        return (
          <DynamicPostMagazineArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case ArticleRenderType.Series:
        return <DynamicSeriesArticle provider={provider} reference={article} />;
      case ArticleRenderType.Style:
        return <DynamicStyleArticle provider={provider} reference={article} />;
    }
  }, [article, isInlinePaywallDomChanged, isShowInlinePaywall, provider, renderType]);

  useEffect(() => {
    if (isShowInlinePaywall !== undefined) {
      setIsContentReady?.(true);
      setEntityId?.(article?.entityId);
    }
    return;
  }, [article.entityId, isShowInlinePaywall, setEntityId, setIsContentReady]);

  return (
    <ArticleRenderContainer $renderType={renderType} ref={articleElementReference}>
      <AuthorAvatarContextProvider
        showBorderColor={
          articleType === articleData.types.column.entityId &&
          renderType !== ArticleRenderType.Generic
        }
      >
        <ThemeProvider theme={defaultTheme}>{handleRenderApplicationArticle()}</ThemeProvider>
      </AuthorAvatarContextProvider>
    </ArticleRenderContainer>
  );
};

Component.displayName = "ArticleRender";
export const ArticleRender = withArticleRenderContext(Component);
